import Link from "next/link";
import { Image } from "@/components/ui/image";
import { AlertTriangle, Building2, Check, CircleCheck, CircleHelp, FileCheck, Info, Users, Wallet, type LucideIcon } from "lucide-react";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { BrandLockup } from "@/components/brand";
import { supportContacts } from "@/lib/support-contacts";

export function Brand() {
  return <BrandLockup context="Licensing portal" />;
}

export function AuthShell({ children, route, footer = "Protected access for verified facility administrators" }: { children: React.ReactNode; route: string; footer?: string }) {
  return <div className="auth-canvas"><Brand /><main className="auth-main">{children}</main><footer className="auth-footer"><span>{footer}</span><span>facility360 account access</span></footer></div>;
}

export function StatusPanel({ tone = "info", title, children }: { tone?: "info" | "success" | "warning" | "danger"; title: string; children: React.ReactNode }) {
  const Icon = tone === "success" ? Check : tone === "info" ? Info : AlertTriangle;
  return <Alert variant={tone === "danger" ? "destructive" : tone}><div className="status-icon"><Icon size={15} /></div><div><AlertTitle>{title}</AlertTitle><AlertDescription>{children}</AlertDescription></div></Alert>;
}

export function PageNotice({ children }: { children: React.ReactNode }) {
  return <Alert className="page-notice"><div className="status-icon"><Info size={15} /></div><div><AlertDescription>{children}</AlertDescription></div></Alert>;
}

/* Facility360 redesign: sign-in and sign-up (Figma frames 06–12). */

function PanelDecoration() {
  return <>
    <Image className="auth-deco auth-deco-top" src="/auth/deco-top.svg" alt="" width={560} height={560} aria-hidden="true" />
    <Image className="auth-deco auth-deco-bottom" src="/auth/deco-bottom.svg" alt="" width={380} height={380} aria-hidden="true" />
  </>;
}

const valuePoints: { icon: LucideIcon; title: string; body: string }[] = [
  { icon: FileCheck, title: "Never miss a renewal", body: "Expiry alerts 60 days ahead and renewal in a few clicks." },
  { icon: Users, title: "Affiliate professionals", body: "Verify practitioners against the Health Worker Registry." },
  { icon: Wallet, title: "Pay with AfyaPay", body: "Invoices and M-Pesa payments, tracked automatically." },
];

function BrandPanel() {
  return <aside className="auth-panel-side" aria-label="About Facility360">
    <PanelDecoration />
    <BrandLockup tone="inverse" size="lg" />
    <div className="auth-panel-spacer" />
    <div className="auth-panel-intro">
      <p className="auth-panel-headline">Run your facility with confidence.</p>
      <p>Your facility’s licenses, practitioner affiliations and payments, in one place.</p>
    </div>
    <ul className="auth-value-list">
      {valuePoints.map(({ icon: Icon, title, body }) => <li key={title}><span className="auth-value-icon"><Icon size={20} /></span><span><strong>{title}</strong><span>{body}</span></span></li>)}
    </ul>
    <div className="auth-preview-card" aria-hidden="true">
      <div className="auth-preview-row">
        <span className="auth-preview-name"><Building2 size={18} />Riverside Maternity</span>
        <span className="auth-badge success"><CircleCheck size={12} />Active</span>
      </div>
      <div className="auth-preview-meter"><span /></div>
      <div className="auth-preview-row small"><span>License valid</span><strong>272 days left</strong></div>
    </div>
    <div className="auth-panel-spacer" />
    <p className="auth-panel-footer">© {new Date().getFullYear()} HealthPro Suite · Secure facility access</p>
  </aside>;
}

export type SignupStep = 1 | 2 | 3 | 4 | 5 | 6 | 7;

/* Registration rail: onboarding_v3 account steps, then the completion steps before the dashboard (Revision 2d). */
const signupSteps: { title: string; detail: string }[] = [
  { title: "Find Facility", detail: "Facility Registration Number" },
  { title: "Confirm Code", detail: "Code Sent to Your Registry Number or Email" },
  { title: "Confirm Facility", detail: "Matched from the Registry" },
  { title: "Create Account", detail: "Email and Password" },
  { title: "Verify Email", detail: "Link Sent to Your Email" },
  { title: "Complete Setup", detail: "Contacts and Bank Accounts" },
  { title: "Done", detail: "Start Using Facility360" },
];

/* Step 6 is one route with three sections; the rail lists them under "Complete setup". */
export const facilityDetailSteps = ["Review Facility", "Contacts", "Bank Accounts"] as const;
const SETUP_STEP = 6;
export type FacilitySubStep = 1 | 2 | 3;

const stepState = (number: number, current: number, finished = false, currentDone = false) =>
  finished || number < current || (currentDone && number === current) ? "done" : number === current ? "current" : "upcoming";

/* `finished`: the whole registration is complete (Done, step 6) — every step ticked.
   `stepDone`: only the current step is complete (e.g. email verified at step 4);
   later steps stay unmarked until they are actually done. */
function SignupProgress({ current, subStep, finished, stepDone }: { current: SignupStep; subStep?: FacilitySubStep; finished?: boolean; stepDone?: boolean }) {
  return <aside className="auth-panel-side rail" aria-label="Registration progress">
    <PanelDecoration />
    <BrandLockup tone="inverse" size="lg" />
    <div className="auth-rail-intro">
      <p className="auth-rail-title">{finished ? "You’re All Set" : "Register Your Facility"}</p>
      <p>{finished ? "Your facility is on Facility360. Pick up the next steps whenever you’re ready." : "About 5 minutes. Keep your ID, email and phone nearby."}</p>
    </div>
    <ol className="auth-rail">
      {signupSteps.map((step, index) => {
        const number = index + 1;
        const state = stepState(number, current, finished, stepDone);
        const isCurrent = number === current;
        return <li key={number} className={state} aria-current={isCurrent ? "step" : undefined}>
          <span className="auth-rail-dot">{state === "done" ? <Check size={14} /> : number}</span>
          <span className="auth-rail-copy">
            <strong>{step.title}</strong>
            <span>{step.detail}{state === "done" && <span className="sr-only"> (complete)</span>}</span>
            {number === SETUP_STEP && isCurrent && subStep && <ol className="auth-rail-sub" aria-label="Setup sections">
              {facilityDetailSteps.map((label, subIndex) => {
                const subState = stepState(subIndex + 1, subStep);
                return <li key={label} className={subState} aria-current={subState === "current" ? "step" : undefined}>
                  <span className="auth-rail-sub-dot">{subState === "done" && <Check size={10} />}</span>{label}{subState === "done" && <span className="sr-only"> (complete)</span>}
                </li>;
              })}
            </ol>}
          </span>
        </li>;
      })}
    </ol>
    <div className="auth-panel-spacer" />
    <div className="auth-help-card">
      <CircleHelp size={18} />
      <span><strong>Need Help Registering?</strong><span>Call {supportContacts.phone} or email <Link href="/contact">{supportContacts.email}</Link> · {supportContacts.hours}</span></span>
    </div>
  </aside>;
}

export function AuthLayout({ variant, step = 1, subStep, finished, stepDone, size = "default", children }: { variant: "sign-in" | "sign-up"; step?: SignupStep; subStep?: FacilitySubStep; finished?: boolean; stepDone?: boolean; size?: "narrow" | "default" | "wide" | "full"; children: React.ReactNode }) {
  return <div className={`auth-layout ${variant}`}>
    {variant === "sign-in" ? <BrandPanel /> : <SignupProgress current={step} subStep={subStep} finished={finished} stepDone={stepDone} />}
    <div className="auth-form-side">
      <div className="auth-form-top">
        <BrandLockup />
        <Link className="auth-help-link" href="/contact"><CircleHelp size={16} /><span>Need help?</span><strong>Contact support</strong></Link>
      </div>
      {variant === "sign-up" && <div className="auth-mobile-progress" aria-hidden="true"><span className="auth-mobile-meter"><span data-step={step} /></span></div>}
      <main className={`auth-form-center ${size}`}>{children}</main>
      <footer className="auth-form-footer">
        <Link href="/faq">Help centre</Link>
        <Link href="/terms">Terms of use</Link>
        <Link href="/privacy">Privacy policy</Link>
      </footer>
    </div>
  </div>;
}

export function AuthHeading({ overline, title, children }: { overline?: string; title: string; children?: React.ReactNode }) {
  return <header className="auth-heading">
    {overline && <p className="auth-overline">{overline}</p>}
    <h1>{title}</h1>
    {children && <p>{children}</p>}
  </header>;
}

export function AuthNote({ icon: Icon, children }: { icon: LucideIcon; children: React.ReactNode }) {
  return <p className="auth-note"><Icon size={16} /><span>{children}</span></p>;
}

export function AuthCallout({ title, children }: { title: string; children: React.ReactNode }) {
  return <div className="auth-callout" role="note"><Info size={18} /><span><strong>{title}</strong><span>{children}</span></span></div>;
}
